import React, { useState, useEffect } from 'react';
import { Modal, Box, TextField, Button, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import './TaskModal.css';
import { Task } from './App';
import ConfirmDeleteModal from './ConfirmDeleteModal'; // new import

interface EditTaskModalProps {
    isOpen: boolean;
    task: Task;
    onClose: () => void;
    onSave: (updatedTask: Task) => void;
    onDelete: (task: Task) => void; // new prop for deletion
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({ isOpen, task, onClose, onSave, onDelete }) => {
    const [title, setTitle] = useState(task.title);
    const [description, setDescription] = useState(task.description);
    const [estimatedLength, setEstimatedLength] = useState<number>(task.estimatedLength);
    const [dueDate, setDueDate] = useState<Date | null>(new Date(task.dueDate));
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('low');
    const [status, setStatus] = useState<'pending' | 'inProgress' | 'done'>('pending');
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false); // new state for confirmation

    // Initialize fields when task changes.
    useEffect(() => {
        setTitle(task.title);
        setDescription(task.description);
        setEstimatedLength(task.estimatedLength);
        setDueDate(new Date(task.dueDate));
        // Optionally, if task objects include priority/status, set them here.
    }, [task]);

    const handleSave = () => {
        if (dueDate) {
            const updatedTask: Task = {
                ...task,
                title,
                description,
                estimatedLength,
                dueDate,
                // Include priority/status if desired.
            };
            onSave(updatedTask);
            onClose();
        }
    };

    const handleDeleteClick = () => {
        setIsConfirmDeleteOpen(true);
    };

    const confirmDelete = () => {
        onDelete(task);
        setIsConfirmDeleteOpen(false);
        onClose();
    };

    const cancelDelete = () => {
        setIsConfirmDeleteOpen(false);
    };

    const isSaveDisabled = !title || !estimatedLength || !dueDate;

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box className="modal-box">
                    <h2>Edit Task</h2>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                    />
                    <div className="inline-fields">
                        <TextField
                            label="Estimated Length (hours)"
                            type="number"
                            value={estimatedLength}
                            onChange={(e) => setEstimatedLength(Number(e.target.value))}
                        />
                        <DatePicker
                            label="Due Date"
                            value={dueDate}
                            onChange={(date) => setDueDate(date)}
                        />
                    </div>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="priority-label">Priority</InputLabel>
                        <Select
                            labelId="priority-label"
                            label="Priority"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                        >
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            label="Status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value as 'pending' | 'inProgress' | 'done')}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="inProgress">In Progress</MenuItem>
                            <MenuItem value="done">Done</MenuItem>
                        </Select>
                    </FormControl>
                    <div className="modal-actions">
                        <Button onClick={onClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteClick} variant="contained" color="error">
                            Delete
                        </Button>
                        <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaveDisabled}>
                            Save
                        </Button>
                    </div>
                </Box>
            </Modal>
            {isConfirmDeleteOpen && (
                <ConfirmDeleteModal
                    isOpen={isConfirmDeleteOpen}
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                />
            )}
        </>
    );
};

export default EditTaskModal;
